import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Box, Center, Loader, Paper, Stack, Text, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { processCheckoutSession } from '@worker/services/MyJobs/api';
import { BASE_PATH } from '@utils/Environment';
import QueryKeys from '@constants/queryKeys';

const CHECKOUT_TYPES = {
  WORKER_IN_NETWORK: 'w_in_network',
} as const;

export default function CheckoutConfirmation() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isProcessing, setIsProcessing] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Prevent duplicate API calls in React StrictMode (development)
  const hasProcessed = useRef(false);

  const sessionId = searchParams.get('sessionId');
  const type = searchParams.get('type');

  useEffect(() => {
    // Skip if already processed (prevents React StrictMode double-call in dev)
    if (hasProcessed.current) {
      return;
    }

    const processPayment = async () => {
      // Validate parameters
      if (!sessionId || !type) {
        setErrorMessage(
          'Invalid checkout session. Missing required parameters.'
        );
        setIsProcessing(false);
        return;
      }

      if (type !== CHECKOUT_TYPES.WORKER_IN_NETWORK) {
        setErrorMessage('Unknown checkout session type.');
        setIsProcessing(false);
        return;
      }

      // Mark as processed before making the API call
      hasProcessed.current = true;

      try {
        const response = await processCheckoutSession(type, sessionId);

        // Invalidate worker profile query to refetch updated data with premium features
        await queryClient.invalidateQueries({
          queryKey: [QueryKeys.WORKER_PROFILE],
        });

        // Show success notification
        notifications.show({
          title: 'Success!',
          message: response.message || 'Payment processed successfully!',
          color: 'green',
        });

        // Redirect to MyJobs with success flag to show In-Network benefits modal
        navigate(`${BASE_PATH}jobs/my-jobs/new?openInNetworkBenefits=1`, {
          replace: true,
        });
      } catch (error: any) {
        console.error('Failed to process checkout session:', error);

        const errorMsg =
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          'Failed to process payment. Please contact support.';

        setErrorMessage(errorMsg);
        setIsProcessing(false);

        notifications.show({
          title: 'Payment Processing Failed',
          message: errorMsg,
          color: 'red',
        });
      }
    };

    processPayment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, type]);

  return (
    <Center h="100vh" p="md">
      <Paper shadow="md" p="xl" radius="md" w={{ base: '100%', sm: 500 }}>
        <Stack align="center" gap="lg">
          {isProcessing ? (
            <>
              <Loader size="xl" color="yellow" />
              <Title order={2} ta="center">
                Processing Your Payment
              </Title>
              <Text c="dimmed" ta="center">
                Please wait while we confirm your payment...
              </Text>
            </>
          ) : (
            <>
              <Title order={2} ta="center" c="red">
                Payment Processing Error
              </Title>
              <Text c="dimmed" ta="center">
                {errorMessage}
              </Text>
              <Text size="sm" c="dimmed" ta="center">
                If you continue to experience issues, please contact support.
              </Text>
              <Box mt="md">
                <Text
                  component="a"
                  href={`${BASE_PATH}jobs/my-jobs/new`}
                  c="blue"
                  td="underline"
                  style={{ cursor: 'pointer' }}
                >
                  Return to My Jobs
                </Text>
              </Box>
            </>
          )}
        </Stack>
      </Paper>
    </Center>
  );
}
